import React, { useState } from "react";

import { useTranslation } from "@refinedev/core";

import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";

import { VerificationCodeInput } from "@/components/auth/VerificationCodeInput";
import { CardButton } from "@/components/common";
import { VERIFY_USER_EMAIL_MUTATION, VerifyEmailInput, VerifyUserEmailData } from "@/utils/GraphQLClient";

import { useMutation } from "urql";

interface EmailVerificationModalProps {
  open: boolean;
  maskedEmail: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  open,
  maskedEmail,
  onClose,
  onSuccess,
}) => {
  const { translate } = useTranslation();

  // urql mutation hook
  const [verifyEmailResult, verifyEmailMutation] = useMutation<VerifyUserEmailData, { input: VerifyEmailInput }>(
    VERIFY_USER_EMAIL_MUTATION
  );
  const isSubmitting = verifyEmailResult.fetching;

  // Form state
  const [verificationCode, setVerificationCode] = useState("");

  // UI state
  const [error, setError] = useState<string | null>(null);

  const handleCodeChange = (code: string) => {
    setVerificationCode(code);
    setError(null);
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      return;
    }

    setError(null);

    try {
      const result = await verifyEmailMutation({
        input: { confirmationCode: verificationCode },
      });

      if (result.error) {
        setError(
          result.error.graphQLErrors?.[0]?.message ??
            result.error.message ??
            translate("pages.profile.changeEmail.verificationError")
        );
        return;
      }

      if (result.data?.verifyUserEmail === "SUCCESS") {
        onSuccess();
      } else {
        setError(translate("pages.profile.changeEmail.verificationError"));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : translate("pages.profile.changeEmail.verificationError"));
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setVerificationCode("");
      setError(null);
      onClose();
    }
  };

  const isSubmitDisabled = verificationCode.length !== 6 || isSubmitting;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="email-verification-modal">
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h3" color="primary">
            {translate("pages.profile.changeEmail.verifyTitle")}
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Close verification modal"
            data-testid="close-button"
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" color="text.secondary" mb={3} textAlign="center">
          {translate("pages.profile.changeEmail.verifyMessage", { email: maskedEmail })}
        </Typography>

        <VerificationCodeInput
          value={verificationCode}
          onChange={handleCodeChange}
          onComplete={handleVerify}
          disabled={isSubmitting}
          autoFocus
        />

        {error && (
          <Typography color="error.dark" variant="body2" mt={2} textAlign="center" data-testid="error-message">
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <CardButton onClick={handleVerify} disabled={isSubmitDisabled} data-testid="verify-button">
          {translate("pages.profile.changeEmail.changeEmailButton")}
        </CardButton>
      </DialogActions>
    </Dialog>
  );
};
